// useSubmit.ts
import * as React from "react"
import type { FormApi, FormSpec } from "./formSpec"
import { applyFieldErrors, extractMessage, safeJson } from "./formHttp"

type Params = {
    spec: FormSpec
    values: Record<string, unknown>
    form: FormApi
    setLoading: (v: boolean) => void
    setErrorGlobal: (v: string) => void
    validateAll: () => Promise<any>
    didSubmitRef: React.MutableRefObject<boolean>
    onSuccess?: (result: unknown, values: Record<string, unknown>) => void
    onError?: (error: unknown) => void
}

function navigateTo(url: string) {
    if (!url) return
    window.location.assign(url)
}

function getHeaderLocation(res: Response): string {
    return res.headers.get("Location") || res.headers.get("location") || ""
}

function getJsonRedirect(payload: unknown): string {
    if (!payload) return ""
    if (typeof payload !== "object") return ""
    if (!("redirect" in payload)) return ""
    const v = (payload as any).redirect
    if (typeof v !== "string") return ""
    return v
}

export function useSubmit({
    spec,
    values,
    form,
    setLoading,
    setErrorGlobal,
    validateAll,
    didSubmitRef,
    onSuccess,
    onError,
}: Params) {
    const submit = spec.submit
    const method = (submit.method || "POST").toUpperCase()

    const handleSubmit = React.useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault()
            didSubmitRef.current = true
            setErrorGlobal("")
            setLoading(true)

            try {
                const fe = await validateAll()
                if (fe && typeof fe === "object" && Object.keys(fe).length > 0) {
                    return
                }

                const res = await fetch(submit.action, {
                    method,
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(values),
                })

                if (res.redirected) {
                    navigateTo(res.url)
                    return
                }

                const loc = getHeaderLocation(res)
                if ((res.status === 301 || res.status === 302 || res.status === 303 || res.status === 307 || res.status === 308) && loc) {
                    navigateTo(loc)
                    return
                }

                const payload = await safeJson(res)

                const jsonRedirect = getJsonRedirect(payload)
                if (jsonRedirect) {
                    navigateTo(jsonRedirect)
                    return
                }

                if (!res.ok) {
                    const msg = extractMessage(payload, submit.errorText || "Fehler")
                    setErrorGlobal(msg)
                    applyFieldErrors(form, payload)
                    return
                }

                if (onSuccess) onSuccess(payload, values)
            } catch (err) {
                setErrorGlobal(submit.errorText || "Fehler")
                if (onError) onError(err)
            } finally {
                setLoading(false)
            }
        },
        [didSubmitRef, form, method, onError, onSuccess, setErrorGlobal, setLoading, submit.action, submit.errorText, validateAll, values]
    )

    return { handleSubmit }
}
